import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { setSnackbarInstance, SnackbarAction, SnackbarMessage, SnackbarType } from '../utils/snackbar'

interface SnackbarContextType {
    messages: SnackbarMessage[]
    showSnackbar: (type: SnackbarType, message: string, duration?: number, action?: SnackbarAction, secondaryAction?: SnackbarAction) => string
    removeSnackbar: (id: string) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<SnackbarMessage[]>([])

    const removeSnackbar = useCallback((id: string) => {
        setMessages((prev) => prev.filter((msg) => msg.id !== id))
    }, [])

    const showSnackbar = useCallback(
        (type: SnackbarType, message: string, duration?: number, action?: SnackbarAction, secondaryAction?: SnackbarAction): string => {
            const id = `${Date.now()}-${Math.random()}`
            const defaultDurations = {
                [SnackbarType.SUCCESS]: 4000,
                [SnackbarType.INFO]: 4000,
                [SnackbarType.WARNING]: 6000,
                [SnackbarType.ERROR]: 8000,
            }

            const finalDuration = duration ?? defaultDurations[type]

            const newMessage: SnackbarMessage = {
                id,
                type,
                message,
                duration: finalDuration,
                action,
                secondaryAction,
            }

            setMessages((prev) => [...prev, newMessage])

            // Auto-remove after duration
            setTimeout(() => {
                removeSnackbar(id)
            }, finalDuration)

            return id
        },
        [removeSnackbar]
    )

    // Initialize the snackbar instance for utility functions
    useEffect(() => {
        setSnackbarInstance({ showSnackbar, removeSnackbar })
    }, [showSnackbar, removeSnackbar])

    return <SnackbarContext.Provider value={{ messages, showSnackbar, removeSnackbar }}>{children}</SnackbarContext.Provider>
}

export const useSnackbar = (): SnackbarContextType => {
    const context = useContext(SnackbarContext)
    if (!context) {
        throw new Error('useSnackbar must be used within SnackbarProvider')
    }
    return context
}
